// What the Tab5 maker, the command in its image (image/tab5.ts) and the
// Studio agree on. No imports: the Worker, Bun in the container and the
// Studio's server all read this file.

/**
 * The Tab5 as Pocket Shell presents an app (pocket-m5's programs/m5stack-tab5,
 * host contract `tab5-home`): the picture is the auxiliary surface, 640x360
 * logical at raster density 1, shown at 2x over the 1280x720 landscape panel,
 * with touch. The primary surface, 400x240, is drawn where nothing scans it
 * out. An app ticks 30 times a second, the contract's one cadence.
 */
export const TAB5_SCREEN = {
  id: "tab5",
  name: "Tab5",
  viewport: [400, 240] as [number, number],
  auxiliary: [640, 360] as [number, number],
  density: 1,
  /** Panel pixels per logical pixel of the picture: 640x360 over 1280x720. */
  presentation: 2,
  auxiliaryOnly: true,
  input: { buttons: false, touch: true },
} as const;

/** A game's files: text where the file is UTF-8, base64 otherwise. Paths are relative to the game's directory. */
export interface GameSource {
  files: Record<string, { text: string } | { base64: string }>;
}

/** What `tab5 build` writes to out/receipt.json: the container's account of its build. Only its warnings are taken from it. */
export interface BuildReceipt {
  id: string;
  title: string;
  version: string | null;
  output: string;
  /** The host contract's id, `tab5-home`. */
  target: string;
  bindingGeneration: number;
  planHash: string;
  /** The profile hash of the host contract the plan resolved against: provenance, which admission does not compare. */
  hostHash: string;
  pocket: { bytes: number; sha256: string };
  js: number;
  pak: number;
  files: number;
  warnings: string[];
}

/**
 * What a `.pocket` says of itself, read from its bytes by the Worker
 * (src/pocket.ts), which keeps it only when its one variant was compiled
 * against the Tab5's host contract at the toolchain's binding generation.
 */
export interface PocketFacts {
  /** The application id, title and bundle name of its identity section. */
  id: string;
  title: string;
  output: string;
  /** The id of the host contract its variant was compiled against, `tab5-home`. */
  target: string;
  bindingGeneration: number;
  /** That host contract's profile hash: provenance, which admission does not compare. */
  hostHash: string;
  bytes: number;
  sha256: string;
}

/**
 * One version of an app the agent saved: its source and its package. The
 * package is at `/p/<nonce>/game.pocket`: the player runs it, and it is the
 * file that is installed on a Tab5. Whoever has the nonce can fetch it.
 */
export interface Version {
  rev: number;
  nonce: string;
  title: string;
  at: number;
  pocket: PocketFacts;
}

/**
 * The agent's last build that is not a version yet: what it is trying out.
 * It runs at `/p/<nonce>/` like a version; saving a version from the same
 * build keeps the nonce, so the address stays the same.
 */
export interface Draft {
  nonce: string;
  title: string;
  at: number;
  /** The package's hash: a later build with the same hash is the same draft. */
  sha256: string;
}

/** GET /games/:id: what the maker holds of one app. */
export interface GameInfo {
  id: string;
  head: Version | null;
  versions: Version[];
  /** A build the agent made after the newest version, or null. */
  draft: Draft | null;
}

/** The data part the agent writes each time it saves a version: `data-version` in the conversation. */
export type VersionPart = Version;

/** The data part written each time the agent's own build or look at the app made a new draft: `data-draft`. */
export type DraftPart = Draft;

/** The longest a version history gets. Older versions leave, their builds with them. */
export const VERSIONS_KEPT = 30;
